import { type JSX } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { CatalogId } from '@kirin/api-client/contracts/ai-instruction-catalog';
import { PoliciesList } from '@/components/policylist/PoliciesList';
import type { PolicySection } from '@/components/policylist/constants';
import { AiInstructionCatalogPage } from '@/components/AiInstructionCatalog/AiInstructionCatalogPage';
import { BuiltInCatalogPage } from '@/components/policylist/components/BuiltInCatalogPage';
import { CustomCapabilityRulesPage } from '@/components/policylist/components/CustomCapabilityRulesPage';
import { GitleaksRulesPage } from '@/components/policylist/components/GitleaksRulesPage';
import {
  useUserPolicies,
  useToggleUserPolicy,
  useUpdateUserPolicySettings,
  useResetUserPolicySettings,
  useUserPolicySettings,
} from '@/hooks/api/useUserPolicies';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import {
  AI_AGENT_INSTRUCTIONS_SLUG,
  EXTENSION_SECURITY_CATALOG_SLUG,
  EXTENSION_SECURITY_CUSTOM_RULES_SLUG,
  POLICIES_AI_INSTRUCTIONS_SLUG,
  POLICIES_ALLOW_BLOCK_SLUG,
  SECRET_DETECTION_GITLEAKS_RULES_SLUG,
} from '@/lib/policyPaths';

const POLICIES_PATH = '/dashboard/policies';

export default function PoliciesTab() {
  const { data: policies, isLoading, error } = useUserPolicies();
  const togglePolicyMutation = useToggleUserPolicy();
  const updateSettingsMutation = useUpdateUserPolicySettings();
  const resetSettingsMutation = useResetUserPolicySettings();
  const usePolicySettings = useUserPolicySettings;

  // Gate the Manage-catalog subroutes behind the feature flags (see org PoliciesTab).
  const { flags, isLoading: isFlagLoading } = useFeatureFlags([
    flagsmithFeatureFlags.AI_INSTRUCTION_CATALOG,
    flagsmithFeatureFlags.CAPABILITY_DETECTION,
    flagsmithFeatureFlags.GITLEAKS_RULE_MANAGEMENT,
  ]);
  // Also gates the "AI Agent Instructions" sub-page (KIR-3982) — see org tab.
  const catalogEnabled =
    flags[flagsmithFeatureFlags.AI_INSTRUCTION_CATALOG] ?? false;
  const capabilityDetectionEnabled =
    flags[flagsmithFeatureFlags.CAPABILITY_DETECTION] ?? false;
  const gitleaksRuleManagementEnabled =
    flags[flagsmithFeatureFlags.GITLEAKS_RULE_MANAGEMENT] ?? false;

  const errorMessage = error
    ? error instanceof Error
      ? error.message
      : String(error)
    : undefined;

  // KIR-3982: mirror the org Policies split (Detection / AI Agent Instructions,
  // flag-gated / Allow / Block Lists).
  const renderPoliciesList = (section: PolicySection): JSX.Element => (
    <PoliciesList
      policies={policies || []}
      loading={isLoading}
      error={errorMessage}
      canEdit={true}
      togglePolicyMutation={togglePolicyMutation}
      updateSettingsMutation={updateSettingsMutation}
      resetSettingsMutation={resetSettingsMutation}
      usePolicySettings={usePolicySettings}
      isOrganizationView={false}
      section={section}
    />
  );

  return (
    <Routes>
      <Route index element={renderPoliciesList('detection')} />
      <Route
        path={POLICIES_AI_INSTRUCTIONS_SLUG}
        element={
          isFlagLoading ? null : catalogEnabled ? (
            renderPoliciesList('ai-instructions')
          ) : (
            <Navigate to={POLICIES_PATH} replace />
          )
        }
      />
      <Route
        path={POLICIES_ALLOW_BLOCK_SLUG}
        element={renderPoliciesList('allow-block')}
      />
      <Route
        path={AI_AGENT_INSTRUCTIONS_SLUG}
        element={
          isFlagLoading ? null : catalogEnabled ? (
            <AiInstructionCatalogPage
              canEdit={true}
              // KIR-4184: return to the AI Agent Instructions sub-page (where the
              // "Manage catalog" link lives), not the Detection Policies root.
              policiesPath={`${POLICIES_PATH}/${POLICIES_AI_INSTRUCTIONS_SLUG}`}
              backLabel="AI Agent Instructions"
              catalog={CatalogId.AgentInstructions}
            />
          ) : (
            <Navigate to={POLICIES_PATH} replace />
          )
        }
      />
      <Route
        path={EXTENSION_SECURITY_CATALOG_SLUG}
        element={
          isFlagLoading ? null : capabilityDetectionEnabled ? (
            <BuiltInCatalogPage policiesPath={POLICIES_PATH} />
          ) : (
            <Navigate to={POLICIES_PATH} replace />
          )
        }
      />
      <Route
        path={EXTENSION_SECURITY_CUSTOM_RULES_SLUG}
        element={
          isFlagLoading ? null : capabilityDetectionEnabled ? (
            <CustomCapabilityRulesPage
              usePolicySettings={usePolicySettings}
              updateSettingsMutation={updateSettingsMutation}
              canEdit={true}
              policiesPath={POLICIES_PATH}
            />
          ) : (
            <Navigate to={POLICIES_PATH} replace />
          )
        }
      />
      <Route
        path={SECRET_DETECTION_GITLEAKS_RULES_SLUG}
        element={
          isFlagLoading ? null : gitleaksRuleManagementEnabled ? (
            <GitleaksRulesPage
              usePolicySettings={usePolicySettings}
              updateSettingsMutation={updateSettingsMutation}
              canEdit={true}
              policiesPath={POLICIES_PATH}
              scopeLabel="your account"
            />
          ) : (
            <Navigate to={POLICIES_PATH} replace />
          )
        }
      />
    </Routes>
  );
}
